/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { Property } from 'csstype';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getStylist } from 'src/core/themes/luisStylist';

export type HeaderBreadcrumbItemProps = {
    /**
     * The text to display.
     */
    text: string;

    /**
     * A callback function for when the item is clicked.
     */
    onClick?: () => void;

    /**
     * Whether the breadcrumb is highlighted or not. That
     * is usually is the case for the last breadcrumb.
     */
    isSelected?: boolean;

    /**
     * Optional secondary text to display.
     */
    secondaryText?: string;

    /**
     * Optional prop to disable the link.
     */
    disabled?: boolean;

    /**
     * A custom render function.
     */
    onRender?: (item: HeaderBreadcrumbItemProps) => JSX.Element;
};

const { styleComponent } = getStylist('GlobalSubHeader');

const BreadcrumbLink = styleComponent<ILinkProps>(Link)<{ isSelected: boolean; disabled: boolean }>(
    'BreadcrumbLink',
    theme => ({
        fontSize: theme.breadcrumbTextSize,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        color: theme.fontColor
    }),
    ({ customProps }, theme) => {
        const isSelectedStyles = customProps.isSelected && {
            fontWeight: theme.activeTextFontWeight as Property.FontWeight,
            textDecoration: 'none !important'
        };

        const disabledStyles = customProps.disabled && { cursor: 'unset', textDecoration: 'none !important' };

        return {
            ...isSelectedStyles,
            ...disabledStyles,
            $nest: { '&:hover,&:active,&:focus,&:visited,&:hover:focus': { color: theme.fontColor } }
        };
    }
);

const SecondaryText = styleComponent(LuisText)('ModelType', theme => ({
    marginTop: 2,
    fontSize: 10,
    color: theme.fontColor,
    marginLeft: theme.secondaryTextMargin
}));

/**
 * Renders a header breadcrumb item. Used in the document
 * application header.
 */
export const HeaderBreadcrumbItem = (props: HeaderBreadcrumbItemProps) => {
    const { text, secondaryText, isSelected, disabled = false, onClick, onRender } = props;

    return onRender ? (
        onRender(props)
    ) : (
        <BreadcrumbLink customProps={{ isSelected, disabled }} onClick={onClick}>
            {text}
            {secondaryText && <SecondaryText>{secondaryText}</SecondaryText>}
        </BreadcrumbLink>
    );
};
